import {ChangeDetectionStrategy, Component, Inject, Injectable, signal} from "@angular/core";
import {ArkivdokumentModell, ArkivnodeId, ArkivNodeModell, SkjermingValideringsfeil} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from "@angular/material/dialog";
import {MatButton} from "@angular/material/button";
import {TranslateModule} from "@ngx-translate/core";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {ArkivVelgNodeKomponent, KanNodeVelges} from "../../komponenter/arkiv-velg-node/arkiv-velg-node";
import {FormControl, Validators} from "@angular/forms";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";

import {ArkivDatatjeneste, ArkivMappenodeDLM} from "felles-ng2/tjenester/datalager/ArkivDatatjeneste";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {i18n, I18nTekst, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {stiTilNodeSomTekst} from "felles-ng2/modell/arkivModellHjelpere";
import {Feilhåndteringsstrategi} from "felles-ng2/tjenester/datalager/datatjeneste";
import {erHttpErrorResponse, erValideringsfeilException, finnValideringsfeilMedFeilkode} from "felles-ng2/infrastruktur/http/httpErrorResponseTyper";
import {feilkoderFraValideringsfeilExceptionSomIkkeoversattStreng} from "felles-ng2/modell/valideringsresultatModellHjelpere";
import {standardFallbackFeilmelding, standardFeilmeldingerForErrorResponsMedFallback} from "felles-ng2/tjenester/ressurser/feilhåndtering";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";

const debug = initDebugger('ArkivFlyttNodeKomponent');

/**
 * Dialog for å flytte arkivnode.
 * MERK at vi forutsetter at noden som flyttes er tilgjengelig i lokalt datalager via {@link ArkivDatatjeneste}.
 */
@Component({
    selector: 'arkiv-flytt-node-dialog',
    templateUrl: 'arkiv-flytt-node-dialog.html',
    styleUrl: 'arkiv-flytt-node-dialog.scss',
    imports: [
        MatButton,
        MatDialogActions,
        MatDialogContent,
        MatDialogTitle,
        TranslateModule,
        ArkivVelgNodeKomponent,
        VisSpinnerDirektiv,
        InlineMeldingKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArkivFlyttNodeDialogKomponent {

    static visFlyttMappe(dialog: MatDialog, mappe: ArkivMappenodeDLM) {
        return ArkivFlyttNodeDialogKomponent.visDialog(dialog, {mappe});
    }

    static visFlyttDokument(dialog: MatDialog, dokument: ArkivdokumentModell) {
        return ArkivFlyttNodeDialogKomponent.visDialog(dialog, {dokument});
    }

    private static visDialog(dialog: MatDialog, parametre: DialogParametre) {
        return dialog.open<ArkivFlyttNodeDialogKomponent, DialogParametre, boolean>(ArkivFlyttNodeDialogKomponent,
          {
              data: parametre,
              panelClass: 'arkiv-flytt-node-dialog-dialog',
              minHeight: '300px',
              minWidth: '550px'
          });
    }

    maalMappKontroll = new FormControl<ArkivMappenodeDLM | null>(null, Validators.required);
    erMappe = true;
    nodeSomFlyttes: ArkivNodeModell;
    feilmelding = signal<string | null>(null);

    lagrer = asynkArbeidPågårSignal();

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                public dialogRef: MatDialogRef<ArkivFlyttNodeDialogKomponent>,
                private avhengigheter: ArkivFlyttNodeDialogKomponentAvhengigheter,
                private oversettelser: Oversettelser,
                private meldingTilBruker: MeldingTilBrukerNg2) {
        if (erIkkeNil(parametre.mappe)) {
            this.erMappe = true;
            this.nodeSomFlyttes = parametre.mappe!;
        } else {
            this.erMappe = false;
            this.nodeSomFlyttes = this.avhengigheter.arkivnode(parametre.dokument!.foreldre.arkivfilnodeId!);
        }
    }

    async flytt() {
        try {
            await this.lagrer.utfør(() => this.avhengigheter.flytt(this.nodeSomFlyttes.id, this.maalMappKontroll.value!.id));
        } catch (e) {
            this.håndterFeil(e);
            return;
        }
        this.meldingTilBruker.infomelding(new I18nTekst(i18n("Flyttet '{kilde}' til '{til}'"), {kilde: stiTilNodeSomTekst(this.nodeSomFlyttes), til: stiTilNodeSomTekst(this.maalMappKontroll.value!)}));
        this.dialogRef.close(true);
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    /** Funksjon som avgjør om node er tilgjengelig for valg i velg-node GUIen.
     * Forhindrer bruker i å flytte mappe til seg selv eller barn av seg selv. */
    kanNodeVelgesFn():KanNodeVelges{
        return node => {
            if (!node.erMappe) return false; //kun mapper kan velges
            if (node.id == this.nodeSomFlyttes.id) return false; //kan ikke flytte til seg selv
            if (node.id == this.nodeSomFlyttes.forelder!.id) return false;  //kan ikke flytt til mappen den allerede ligger i
            while (!node.erRotMappe){
                if (node.forelder!.id == this.nodeSomFlyttes.id) return false; //kan ikke flytte til barn av seg selv
                node = node.forelder!;
            }
            return true;
        };
    }

    //private hjelpemetoder

    private håndterFeil(feil: any) {
        let melding: I18nTekst;
        if (finnValideringsfeilMedFeilkode(feil, SkjermingValideringsfeil.medlem_skal_signere_dokument))
            melding = new I18nTekst(i18n('Kan ikke flytte et dokument som skal signeres til en mappe som den som skal signere ikke har tilgang til'));
        else if (erValideringsfeilException(feil))
            melding = new I18nTekst(i18n('En valideringsfeil oppstod under flytting ({feilkode})'), {feilkode: feilkoderFraValideringsfeilExceptionSomIkkeoversattStreng(feil)});
        else if (erHttpErrorResponse(feil))
            melding = standardFeilmeldingerForErrorResponsMedFallback(feil);
        else
            melding = standardFallbackFeilmelding(feil);

        this.feilmelding.set(melding.oversett(this.oversettelser));
    }
}

export interface DialogParametre {
    mappe?: ArkivMappenodeDLM;
    dokument?: ArkivdokumentModell;
}

@Injectable()
export class ArkivFlyttNodeDialogKomponentAvhengigheter {

    constructor(private arkivDatatjeneste: ArkivDatatjeneste) {
    }

    arkivnode(id: ArkivnodeId): ArkivNodeModell {
        return this.arkivDatatjeneste.hentFraLokallager(id);
    }

    async flytt(kildeId: ArkivnodeId, målMappeId: ArkivnodeId): Promise<void> {
        await this.arkivDatatjeneste.flytt(kildeId, målMappeId, Feilhåndteringsstrategi.propagerSomError);
    }
}
